import { LoanApiService } from './../../services/loan-api.service';
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WithDestroy } from '@lib/shared';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class LoanStepperService extends WithDestroy() implements OnDestroy {
  public loanId: number;
  public percentCompletion = 0;
  public uiStateDataStore = new BehaviorSubject<any>({});
  public uiStateData$ = this.uiStateDataStore.asObservable();
  public stopNavigation = false;
  public steps = [
    {
      route: 'about-your-mortgage',
      label: 'Mortgage Info',
      complete: false,
      key: 'mortgage'
    },
    {
      route: 'about-you',
      label: 'Personal Info',
      complete: false,
      key: 'aboutYou'
    },
    {
      route: 'coborrower',
      label: 'Co-Borrowers',
      complete: false,
      key: 'aboutCoBorrower'
    },
    {
      route: 'income',
      label: 'Income',
      complete: false,
      key: 'income'
    },
    {
      route: 'assets',
      label: 'Assets',
      complete: false,
      key: 'assets'
    },
    {
      route: 'liabilities',
      label: 'Liabilities',
      complete: false,
      key: 'liabilities'
    },
    {
      route: 'real-estate',
      label: 'Real Estate',
      complete: false,
      key: 'realEstate'
    },
    {
      route: 'declarations',
      label: 'Declarations',
      complete: false,
      key: 'declarations'
    },
    {
      route: 'demographic-info',
      label: 'Demographics',
      complete: false,
      key: 'demographic'
    },
    {
      route: 'credit',
      label: 'Credit',
      complete: false,
      key: 'credit'
    },
    {
      route: 'questionnaire',
      label: 'Additional Questions',
      complete: false,
      key: 'questionnaire'
    },
    {
      route: 'review',
      label: 'Review and Submit',
      complete: false,
      key: 'review'
    }
  ];

  private posConfig: any;
  public loanPurpose: string;
  readonly PURCHASE = 'purchase';
  public pendingTasks$ = new BehaviorSubject(null);
  public pendingTasksData$ = this.pendingTasks$.asObservable();

  constructor(public route: ActivatedRoute, private router: Router, private loanApiService: LoanApiService) {
    super();
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params: any) => {
      this.loanId = params.loanId;
      this.intializeSteps();
    });

    this.posConfig = this.route.snapshot.data.posOrgConfig.posConfig;
  }

  private intializeSteps() {
    // Reset stepper data first
    this.steps.forEach((step) => (step.complete = false));
    this.percentCompletion = 0;
    this.stopNavigation = false;
    this.pendingTasks$.next(null);
    this.loanApiService.getUiState(this.loanId).subscribe((value) => {
      this.uiStateDataStore.next(value);
      this.steps.forEach((step) => (step.complete = value[step.key] || false));
      if (this.router.url && this.router.url.includes('application')) {
        this.setPercentCompletion();
        // Redirect to first incomplete screen (current route gets activated before redirection)
        const incompleteStep = this.steps.find(
          (step) => !step.complete && (this.posConfig[this.loanPurpose][step.key] || step.key === 'review')
        );
        const loan = this.route.snapshot.data.loans.find((loan) => loan.LoanId === +this.loanId);
        if (incompleteStep && loan?.inviteType !== 'REVIEW_ONLY') {
          this.router.navigate(['application', incompleteStep.route], { relativeTo: this.route });
        }
      }
    });
  }

  public markStepAsCompleted(route: string) {
    if (route == 'declarations-urla') {
      route = 'declarations';
    }
    const stepIndex = this.steps.findIndex((subStep) => subStep.route === route);
    if (stepIndex >= 0) {
      this.steps[stepIndex].complete = true;
      this.updateUiState(this.steps[stepIndex]);
      this.setPercentCompletion();
    }
  }

  public markStepAsIncomplete(route: string) {
    const stepIndex = this.steps.findIndex((subStep) => subStep.route === route);
    if (stepIndex >= 0) {
      this.steps[stepIndex].complete = false;
      this.setPercentCompletion();
    }
  }

  private updateUiState(step) {
    const stateData = this.uiStateDataStore.getValue();
    stateData[step.key] = true;
    this.uiStateDataStore.next(stateData);
  }

  public setReviewTooltipFlag() {
    this.loanApiService.updateUiState(this.loanId, { skipReviewTooltip: true }).subscribe((value) => {
      this.uiStateDataStore.next(value);
    });
  }

  private setPercentCompletion() {
    this.loanPurpose = this.loanPurpose ? this.loanPurpose.toLowerCase() : this.PURCHASE;
    this.percentCompletion = 0;
    if (this.loanPurpose) {
      let completedSum = 0;
      let totalSum = 0;
      this.steps.forEach((step) => {
        if (this.posConfig[this.loanPurpose][step.key] || step.key === 'review') {
          // same weightage for all steps temporarily
          const keyWeightage = 10; // step.key === 'realEstate' || step.key === 'review' ? 5 : 10;
          if (step.complete) {
            completedSum += keyWeightage;
          }
          totalSum += keyWeightage;
        }
      });
      this.percentCompletion = Math.floor((completedSum * 100) / totalSum);
    }
  }

  public getPreviousPage(route: ActivatedRoute, loanPurposeType: string) {
    loanPurposeType = loanPurposeType ? loanPurposeType.toLowerCase() : this.PURCHASE;
    const path = route.snapshot.routeConfig.path;
    const currIndex = this.steps.findIndex((step) => step.route === path);
    const previousStep = this.getPreviousStep(currIndex, loanPurposeType);
    return previousStep;
  }

  private getPreviousStep(stepIndex: number, loanPurposeType: string) {
    for (let i = stepIndex - 1; i >= 0; i--) {
      if (this.posConfig[loanPurposeType][this.steps[i].key]) {
        return this.steps[i];
      }
    }
    return this.steps[0];
  }

  public getNextPage(route: ActivatedRoute, loanPurposeType: string) {
    loanPurposeType = loanPurposeType ? loanPurposeType.toLowerCase() : this.PURCHASE;
    let path = route.snapshot.routeConfig.path;
    if (path === 'declarations-urla') {
      // temporary code
      path = 'declarations';
    }
    const currIndex = this.steps.findIndex((step) => step.route === path);
    const nextStep = this.getNextStep(currIndex, loanPurposeType);
    return nextStep;
  }

  private getNextStep(stepIndex: number, loanPurposeType: string) {
    for (let i = stepIndex + 1; i < this.steps.length; i++) {
      if (this.posConfig[loanPurposeType][this.steps[i].key]) {
        return this.steps[i];
      }
    }
    return this.steps[this.steps.length - 1];
  }

  public next(route: ActivatedRoute, loanPurposeType: string) {
    loanPurposeType = loanPurposeType ? loanPurposeType.toLowerCase() : this.PURCHASE;
    let path = route.snapshot.routeConfig.path;
    if (path === 'declarations-urla') {
      // temporary code
      path = 'declarations';
    }
    if (path === 'review') {
      this.router.navigate(['../review'], { relativeTo: route });
    } else {
      const currIndex = this.steps.findIndex((step) => step.route === path);
      const redirectPath = this.getNextStep(currIndex, loanPurposeType).route;
      this.router.navigate([`../${redirectPath}`], { relativeTo: route });
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
